import {
  Drawer,
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  Button,
  Divider,
  Space,
  message,
  Card,
  Typography,
  Flex,
} from "antd";
import React, { useCallback, useEffect } from "react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../context/theme/context";
import { ProductOptionCategory } from "../graphql/generated/schemas";
import {
  GetAllMyProductOptionCategoriesDocument,
  useUpdateProductOptionCategoryMutation,
  useCreateProductOptionCategoryMutation,
} from "../graphql/generated/hooks";

export type Props = {
  open: boolean;
  onClose: () => void;
  editMode?: boolean;
  categoryToEdit?: Partial<ProductOptionCategory>;
};

function ProductOptionsCategoryDrawer({
  open,
  onClose,
  editMode = false,
  categoryToEdit,
}: Props) {
  const { t, i18n } = useTranslation();
  const themeContext = useThemeContext();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [updateCategory] = useUpdateProductOptionCategoryMutation();
  const [addCategory] = useCreateProductOptionCategoryMutation();

  const resetForm = useCallback(() => {
    form.resetFields();
    // Form.List initialValue is only applied on first mount,
    // so we explicitly reset the list to a clean default.
    form.setFieldsValue({ productOptions: [{}] });
  }, [form]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (values: any) => {
    const min = Number(values?.min);
    const max = Number(values?.max);

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      messageApi.error(t("validation.productOptionCategory.minMax.required"));
      return;
    }
    if (min < 0 || max < 0) {
      messageApi.error(t("validation.productOptionCategory.minMax.nonNegative"));
      return;
    }
    if (max < min) {
      messageApi.error(t("validation.productOptionCategory.max.gteMin"));
      return;
    }

    if (editMode) {
      await updateCategory({
        variables: {
          id: categoryToEdit?.id ?? 0,
          name: values.name,
          nameAr: values.nameAr,
          min,
          max,
          productOptions: values.productOptions.map((opt: any) => ({
            name: opt.name || "",
            nameAr: opt.nameAr || "",
            description: opt.description,
            descriptionAr: opt.descriptionAr,
            price: +opt.price,
          })),
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: GetAllMyProductOptionCategoriesDocument },
        ],
      })
        .then(() => {
          messageApi.success(t("message.success.data_updated"));
          resetForm();
          onClose();
        })
        .catch((error) => {
          messageApi.error(t("message.error.data_update_failed"));
        });
    } else {
      await addCategory({
        variables: {
          name: values.name,
          nameAr: values.nameAr,
          min,
          max,
          productOptions: values.productOptions.map((opt: any) => ({
            name: opt.name || "",
            nameAr: opt.nameAr || "",
            description: opt.description,
            descriptionAr: opt.descriptionAr,
            price: +opt.price,
          })),
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: GetAllMyProductOptionCategoriesDocument },
        ],
      })
        .then(() => {
          messageApi.success(t("message.success.data_created"));
          resetForm();
          onClose();
        })
        .catch((error) => {
          messageApi.error(t("message.error.data_create_failed"));
        });
    }
  };

  useEffect(() => {
    if (!open) return;
    if (categoryToEdit) form.setFieldsValue(categoryToEdit);
    else resetForm();
  }, [categoryToEdit, form, open, resetForm]);

  return (
    <Drawer
      title={
        editMode
          ? t("title.edit_product_option_category")
          : t("title.add_product_option_category")
      }
      placement={i18n.dir() === "rtl" ? "left" : "right"}
      destroyOnHidden
      onClose={handleClose}
      open={open}
      width="50%"
      extra={
        <Space>
          <Button onClick={handleClose}>{t("button.cancel")}</Button>
          <Button
            onClick={() => {
              form.submit();
            }}
            type="primary"
          >
            {t("button.save")}
          </Button>
        </Space>
      }
    >
      {contextHolder}
      {open && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          // initialValues={categoryToEdit}
        >
          <Typography.Title level={4}>
            {t("title.add_category_details")}
          </Typography.Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t("label.name")}
                name="name"
                rules={[{ required: true, message: t("message.error.field_required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t("label.name_ar")}
                name="nameAr"
                rules={[{ required: true, message: t("message.error.field_required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                tooltip={t("tooltip.min")}
                label={t("label.min")}
                name="min"
                rules={[
                  { required: true, message: t("message.error.field_required") },
                  {
                    validator: async (_, value) => {
                      if (value === undefined || value === null || value === "") return;
                      const n = Number(value);
                      if (!Number.isFinite(n)) return;
                      if (n < 0)
                        throw new Error(
                          t("validation.productOptionCategory.minMax.nonNegative")
                        );
                    },
                  },
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                tooltip={t("tooltip.max")}
                label={t("label.max")}
                name="max"
                dependencies={["min"]}
                rules={[
                  { required: true, message: t("message.error.field_required") },
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (value === undefined || value === null || value === "") return;
                      const maxVal = Number(value);
                      const minVal = Number(getFieldValue("min"));
                      if (!Number.isFinite(maxVal)) return;
                      if (maxVal < 0)
                        throw new Error(
                          t("validation.productOptionCategory.minMax.nonNegative")
                        );
                      if (Number.isFinite(minVal) && maxVal < minVal)
                        throw new Error(t("validation.productOptionCategory.max.gteMin"));
                    },
                  }),
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
            </Col>
          </Row>
          <Divider />
          <Typography.Title level={4}>
            {t("title.add_option_details")}
          </Typography.Title>
          <Form.List name={["productOptions"]} initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Card
                    key={key}
                    style={{
                      backgroundColor:
                        themeContext.state.mode === "dark" ? "#1f1f1f" : "",
                      marginBottom: 16,
                    }}
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label={t("label.name")}
                          name={[name, "name"]}
                          rules={[
                            {
                              required: true,
                              message: t("message.error.field_required"),
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={t("label.name_ar")}
                          name={[name, "nameAr"]}
                          rules={[
                            {
                              required: true,
                              message: t("message.error.field_required"),
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={t("label.description")}
                          name={[name, "description"]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={t("label.description_ar")}
                          name={[name, "descriptionAr"]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={t("label.price")}
                          name={[name, "price"]}
                          rules={[
                            {
                              required: true,
                              message: t("message.error.field_required"),
                            },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Flex
                          align="center"
                          justify="center"
                          style={{ height: "100%" }}
                        >
                          <Button
                            color="danger"
                            onClick={() => remove(name)}
                            icon={<CloseOutlined />}
                          >
                            {t("button.remove_option")}
                          </Button>
                        </Flex>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                  >
                    {t("button.add_new_option")}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      )}
    </Drawer>
  );
}

export default ProductOptionsCategoryDrawer;
